/**
 * Résolution de la bulle de notification d'achat reçue sur Ably : qui voit quoi (acheteur,
 * spectateur, présentateur) et avec quel contenu. Le replay evergreen reçoit un payload assaini
 * côté back (pas d'acheteur, pas de message privé) : le libellé fourni par l'appelant ne sert que
 * de repli si le message public de la source n'a pas été conservé.
 */

export type PurchaseNotificationViewer = 'ATTENDEE' | 'ADMIN';

export type PurchaseNotificationBlock = Record<string, unknown>;

export type PurchaseNotificationPayload = {
  evergreenReplay?: unknown;
  buyerEmail?: unknown;
  public?: unknown;
  private?: unknown;
};

export type PurchaseNotificationDisplay = {
  message: string;
  icon: string;
  showIcon: boolean;
  isBuyer: boolean;
  /** Bloc dont proviennent les couleurs de la bulle (privé pour l'acheteur, public sinon). */
  colorSource: PurchaseNotificationBlock | undefined;
};

const DEFAULT_ICON = 'award';

const asBlock = (value: unknown): PurchaseNotificationBlock | undefined =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as PurchaseNotificationBlock)
    : undefined;

const messageOf = (block: PurchaseNotificationBlock | undefined): string | null =>
  typeof block?.message === 'string' && block.message !== '' ? block.message : null;

const iconOf = (block: PurchaseNotificationBlock | undefined): string =>
  typeof block?.icon === 'string' && block.icon !== '' ? block.icon : DEFAULT_ICON;

const showIconOf = (block: PurchaseNotificationBlock | undefined): boolean =>
  typeof block?.displayIcon === 'boolean' ? block.displayIcon : true;

/**
 * Renvoie `null` quand il n'y a rien à afficher pour ce spectateur.
 */
export function resolvePurchaseNotificationDisplay(
  payload: PurchaseNotificationPayload | null | undefined,
  options: {
    viewer: PurchaseNotificationViewer;
    attendeeEmail?: string;
    /** Libellé i18n de repli quand un replay evergreen arrive sans message public. */
    evergreenMessage: string;
  },
): PurchaseNotificationDisplay | null {
  if (payload == null || typeof payload !== 'object') return null;

  const isEvergreenReplay = payload.evergreenReplay === true;
  const publicBlock = asBlock(payload.public);

  if (options.viewer === 'ADMIN') {
    const message = messageOf(publicBlock) ?? (isEvergreenReplay ? options.evergreenMessage : null);
    if (!message) return null;
    return {
      message,
      icon: iconOf(publicBlock),
      showIcon: showIconOf(publicBlock),
      isBuyer: false,
      colorSource: publicBlock,
    };
  }

  const buyerEmail = typeof payload.buyerEmail === 'string' ? payload.buyerEmail : '';
  const attendeeEmail = options.attendeeEmail ?? '';

  // Hors evergreen, sans les deux emails on ne sait pas distinguer l'acheteur du reste de
  // l'audience : on préfère ne rien afficher plutôt que d'adresser le message privé au mauvais
  // spectateur.
  if (!isEvergreenReplay && !(attendeeEmail && buyerEmail)) return null;

  const isBuyer = !isEvergreenReplay && buyerEmail === attendeeEmail;
  const sourceBlock = isBuyer ? asBlock(payload.private) : publicBlock;
  const message = messageOf(sourceBlock) ?? (isEvergreenReplay ? options.evergreenMessage : null);
  if (!message) return null;

  return {
    message,
    icon: iconOf(sourceBlock),
    showIcon: showIconOf(sourceBlock),
    isBuyer,
    colorSource: sourceBlock,
  };
}

export interface PurchaseNotificationOfferColors {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
  iconBackgroundColor: string;
}

const COLOR_FIELDS = ['backgroundColor', 'borderColor', 'textColor', 'iconColor', 'backgroundIcon'];

/**
 * Le back recopie les couleurs de l'offre dans le payload. Sans aucune couleur (notification
 * ajoutée à la main dans l'evergreen), la bulle suit le thème du webinaire.
 */
export function extractPurchaseNotificationOfferColors(
  payload: PurchaseNotificationBlock | undefined | null,
): PurchaseNotificationOfferColors | undefined {
  if (!payload) return undefined;
  const hasColor = COLOR_FIELDS.some(
    (field) => typeof payload[field] === 'string' && payload[field] !== '',
  );
  if (!hasColor) return undefined;
  return {
    backgroundColor: (payload.backgroundColor as string) || '#FFFFFF',
    borderColor: (payload.borderColor as string) || 'transparent',
    textColor: (payload.textColor as string) || '#000000',
    iconColor: (payload.iconColor as string) || '#000000',
    iconBackgroundColor: (payload.backgroundIcon as string) || '#000000',
  };
}
